import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
// @mui
import Typography from '@mui/material/Typography';
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { IconButton, Tooltip } from '@mui/material';
import {useTheme} from '@mui/material/styles';
// components
import { fCurrencyEuro, fPercentEuro } from '../../utils/format-number';
import Iconify from '../../components/iconify';
import { useSettingsContext } from '../../components/settings';

import { getFinancialData } from '../../components/upload';
import FinancialPercentOverview from './financial-percent-overview';
import ChartRadar from './charts/chart-radar';
import AnalysisService from '../../services/analysis';
import ClickableTooltip from '../../components/tooltip/clickable-tooltip';

    // Datos generales para el gráfico de radar
    const ingresosCategories = [...Array(4)].map((_, i) => ({
    category: [
        'duro',
        'blando',
        'movimientos',
        'extraordinarios',
    ][i],
    average: [
        48.52,
        7.48,
        37.7,
        6.3
    ][i],
    }))

    // Datos generales para el gráfico de radar
    const gastosCategories = [...Array(4)].map((_, i) => ({
    category: [
        'básico',
        'discrecional',
        'movimientos',
        'extraordinarios',
    ][i],
    average: [
        23.05,
        21.45,
        51.53,
        3.97
    ][i],
    }))

export default function FinancialConsumingAnalitics() {
    const navigate = useNavigate();
    const settings = useSettingsContext()
    const theme = useTheme();
    
    const [resumen, setResumen] = useState(null);
    const [compIngresos, setCompIngresos] = useState([]);
    const [compGastos, setCompGastos] = useState([]);
    const [compIngresosPrompt, setCompIngresosPrompt] = useState('');
    const [compGastosPrompt, setcompGastosPrompt] = useState('');
    const [ingresosResume, setIngresosResume] = useState('');
    const [gastosResume, setGastosResume] = useState('');

    useEffect(() => {
        try {
            const data = getFinancialData();
            if (!data) throw new Error('No data');
            setResumen(data);
            settings.onFiles(); // Solo si tienes datos
            settings.onValid();
        } catch (error) {
            console.warn('No se encontraron datos. Redirigiendo al inicio...');
            navigate('/financial');
        }
    }, [navigate, settings]);

    useEffect(() => {
        if (!resumen) return;

        const fetchComparativeAnalysis = async () => {
            try {
                // Preparar datos de ingresos
                const ingresosComparativa = [
                    resumen.abono.porc_abono_duro || 0,
                    resumen.abono.porc_abono_blando || 0,
                    resumen.abono.porc_abono_movimientos || 0,
                    100 - ((resumen.abono.porc_abono_duro || 0) + (resumen.abono.porc_abono_blando || 0) + (resumen.abono.porc_abono_movimientos || 0))
                ];
                
                // Preparar datos de gastos
                const gastosComparativa = [
                    resumen.gasto.porc_gasto_basico || 0,
                    resumen.gasto.porc_gasto_discrecional || 0,
                    (resumen.gasto.porc_gasto_movimientos || 0) + (resumen.gasto.porc_gasto_traspaso || 0),
                    100 - ((resumen.gasto.porc_gasto_basico || 0) + (resumen.gasto.porc_gasto_discrecional || 0) + (resumen.gasto.porc_gasto_movimientos || 0) + (resumen.gasto.porc_gasto_traspaso || 0))
                ].map(v => v > 0 ? v : 0);

                setCompIngresos(ingresosComparativa);
                setCompGastos(gastosComparativa);

                // Llamar al backend para análisis de ingresos
                const incomeResult = await AnalysisService.getComparativeAnalysis(
                    'income',
                    ingresosCategories.map(item => item.category),
                    ingresosComparativa,
                    ingresosCategories.map(item => item.average)
                );
                setIngresosResume(incomeResult.analysis);

                // Llamar al backend para análisis de gastos
                const expenseResult = await AnalysisService.getComparativeAnalysis(
                    'expense',
                    gastosCategories.map(item => item.category),
                    gastosComparativa,
                    gastosCategories.map(item => item.average)
                );
                setGastosResume(expenseResult.analysis);

            } catch (error) {
                console.error('Error al obtener análisis comparativo:', error);
                setIngresosResume('Error al cargar el análisis de ingresos');
                setGastosResume('Error al cargar el análisis de gastos');
            }
        };

        fetchComparativeAnalysis();
    }, [resumen]);

    if (!resumen) return null;

    const PositiveIcon = "pepicons-pop:thumbs-up-circle";
    const NegativeIcon = "pepicons-pop:thumbs-down-circle";
    const rLastIndex = resumen.fecha.length - 1;

    const desgloseIngresos = [
        { label: 'Trabajo', totalAmount: [Math.floor(resumen.desglose.ingreso_trabajo.mes_actual.monto) || '0', Math.floor(resumen.desglose.ingreso_trabajo.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.ingreso_trabajo.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.ingreso_trabajo.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Pensión y subsidio', totalAmount: [Math.floor(resumen.desglose.ingreso_pension.mes_actual.monto) || '0', Math.floor(resumen.desglose.ingreso_pension.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.ingreso_pension.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.ingreso_pension.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Ahorro', totalAmount: [Math.floor(resumen.desglose.ingreso_ahorro.mes_actual.monto) || '0', Math.floor(resumen.desglose.ingreso_ahorro.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.ingreso_ahorro.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.ingreso_ahorro.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Facturación', totalAmount: [Math.floor(resumen.desglose.ingreso_facturacion.mes_actual.monto) || '0', Math.floor(resumen.desglose.ingreso_facturacion.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.ingreso_facturacion.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.ingreso_facturacion.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Efectivo', totalAmount: [Math.floor(resumen.desglose.ingreso_efectivo.mes_actual.monto) || '0', Math.floor(resumen.desglose.ingreso_efectivo.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.ingreso_efectivo.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.ingreso_efectivo.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Transferencias y traspasos', totalAmount: [Math.floor(resumen.desglose.ingreso_transferencias.mes_actual.monto) || '0', Math.floor(resumen.desglose.ingreso_transferencias.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.ingreso_transferencias.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.ingreso_transferencias.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Devolución y anulación', totalAmount: [Math.floor(resumen.desglose.ingreso_anulacion.mes_actual.monto) || '0', Math.floor(resumen.desglose.ingreso_anulacion.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.ingreso_anulacion.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.ingreso_anulacion.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Gambling', totalAmount: [Math.floor(resumen.desglose.ingreso_gambling.mes_actual.monto) || '0', Math.floor(resumen.desglose.ingreso_gambling.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.ingreso_gambling.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.ingreso_gambling.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Resto', totalAmount: [Math.floor(resumen.desglose.ingreso_resto.mes_actual.monto) || '0', Math.floor(resumen.desglose.ingreso_resto.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.ingreso_resto.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.ingreso_resto.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Financiación', totalAmount: [Math.floor(resumen.desglose.ingreso_financiacion.mes_actual.monto) || '0', Math.floor(resumen.desglose.ingreso_financiacion.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.ingreso_financiacion.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.ingreso_financiacion.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Impuestos', totalAmount: [Math.floor(resumen.desglose.ingreso_impuestos.mes_actual.monto) || '0', Math.floor(resumen.desglose.ingreso_impuestos.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.ingreso_impuestos.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.ingreso_impuestos.meses_previos.porcentaje * 100)  || '0'] },
    ];

    let subDesgloseGastoBasico = [
        { label: 'Alquiler', totalAmount: [Math.abs(Math.floor(resumen.desglose.gasto_alquiler.mes_actual.monto)) || '0', (Math.floor(resumen.desglose.gasto_alquiler.meses_previos.promedio)) || '0'], value: [Math.floor(resumen.desglose.gasto_alquiler.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_alquiler.meses_previos.porcentaje * 100)  || '0']},
        { label: 'Hogar', totalAmount: [(Math.floor(resumen.desglose.gasto_hogar.mes_actual.monto)) || '0', (Math.floor(resumen.desglose.gasto_hogar.meses_previos.promedio)) || '0'], value: [Math.floor(resumen.desglose.gasto_hogar.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_hogar.meses_previos.porcentaje * 100)  || '0']},
        { label: 'Transporte público', totalAmount: [(Math.floor(resumen.desglose.gasto_trans_publico.mes_actual.monto)) || '0', (Math.floor(resumen.desglose.gasto_trans_publico.meses_previos.promedio)) || '0'], value: [Math.floor(resumen.desglose.gasto_trans_publico.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_trans_publico.meses_previos.porcentaje * 100)  || '0']},
        { label: 'Transporte privado y gasolina', totalAmount: [Math.floor(resumen.desglose.gasto_trans_privado.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_trans_privado.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_trans_privado.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_trans_privado.meses_previos.porcentaje * 100)  || '0']},
        { label: 'Salud', totalAmount: [(Math.floor(resumen.desglose.gasto_salud.mes_actual.monto)) || '0', (Math.floor(resumen.desglose.gasto_salud.meses_previos.promedio)) || '0'], value: [Math.floor(resumen.desglose.gasto_salud.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_salud.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Supermercado', totalAmount: [Math.floor(resumen.desglose.gasto_supermercado.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_supermercado.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_supermercado.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_supermercado.meses_previos.porcentaje * 100)  || '0'] },
        
    ]

    subDesgloseGastoBasico = DesgloseAbsoluto(subDesgloseGastoBasico);
    subDesgloseGastoBasico = subDesgloseGastoBasico.filter((data) => data.totalAmount[0] > 0 || data.totalAmount[1] > 0)

    let desgloseGastoBasico = [
        { label: 'Consumo indispensable', totalAmount: [Math.abs(Math.floor(resumen.desglose.gasto_consumo_indispensable.mes_actual.monto)) || '0', (Math.floor(resumen.desglose.gasto_consumo_indispensable.meses_previos.promedio)) || '0'], value: [Math.floor(resumen.desglose.gasto_consumo_indispensable.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_consumo_indispensable.meses_previos.porcentaje * 100)  || '0'], sub: subDesgloseGastoBasico },
        { label: 'Recibos', totalAmount: [Math.floor(resumen.desglose.gasto_recibos.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_recibos.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_recibos.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_recibos.meses_previos.porcentaje * 100)  || '0']},
        { label: 'Comisiones', totalAmount: [Math.abs(Math.floor(resumen.desglose.gasto_comisiones.monto)) || '0', (Math.floor(resumen.desglose.gasto_comisiones.meses_previos.promedio)) || '0'], value: [Math.floor(resumen.desglose.gasto_comisiones.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_comisiones.meses_previos.porcentaje * 100)  || '0']},
        { label: 'Impuestos', totalAmount: [Math.floor(resumen.desglose.gasto_impuestos.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_impuestos.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_impuestos.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_impuestos.meses_previos.porcentaje * 100)  || '0']},
        
        // { label: 'Colegio', totalAmount: [Math.floor(resumen.desglose.gasto_colegio.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_colegio.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_colegio.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_colegio.meses_previos.porcentaje * 100)  || '0'] },
        // { label: 'Mascota', totalAmount: [Math.floor(resumen.desglose.gasto_mascota.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_mascota.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_mascota.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_mascota.meses_previos.porcentaje * 100)  || '0']},
        // { label: 'Otros', totalAmount: [Math.floor(resumen.desglose.gasto_otros_basico.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_otros_basico.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_otros_basico.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_otros_basico.meses_previos.porcentaje * 100)  || '0']},
    ]

    let subDesgloseGastoDiscrecional = [
        { label: 'Restaurantes y fast food', totalAmount: [Math.floor(resumen.desglose.gasto_restauracion.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_restauracion.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_restauracion.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_restauracion.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Ocio', totalAmount: [Math.floor(resumen.desglose.gasto_ocio.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_ocio.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_ocio.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_ocio.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Suscripciones y gaming', totalAmount: [Math.floor(resumen.desglose.gasto_suscripciones_gaming.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_suscripciones_gaming.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_suscripciones_gaming.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_suscripciones_gaming.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Cultura', totalAmount: [Math.floor(resumen.desglose.gasto_cultura.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_cultura.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_cultura.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_cultura.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Ropa, deporte y belleza', totalAmount: [Math.floor(resumen.desglose.gasto_personal.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_personal.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_personal.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_personal.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Viajes', totalAmount: [Math.floor(resumen.desglose.gasto_viajes.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_viajes.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_viajes.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_viajes.meses_previos.porcentaje * 100)  || '0'] },

    ]

    console.log(resumen.desglose)

    subDesgloseGastoDiscrecional = DesgloseAbsoluto(subDesgloseGastoDiscrecional);
    subDesgloseGastoDiscrecional = subDesgloseGastoDiscrecional.filter((data) => data.totalAmount[0] > 0 || data.totalAmount[1] > 0)

    let desgloseGastoDiscrecional = [
        { label: 'Consumo variado', totalAmount: [Math.floor(resumen.desglose.gasto_consumo_variado.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_consumo_variado.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_consumo_variado.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_consumo_variado.meses_previos.porcentaje * 100)  || '0'], sub: subDesgloseGastoDiscrecional },
        { label: 'Tarjeta crédito', totalAmount: [Math.floor(resumen.desglose.gasto_tarjeta_credito.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_tarjeta_credito.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_tarjeta_credito.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_tarjeta_credito.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Gastos TPV', totalAmount: [Math.floor(resumen.desglose.gasto_gastos_tpv.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_gastos_tpv.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_gastos_tpv.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_gastos_tpv.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Gambling', totalAmount: [Math.floor(resumen.desglose.gasto_gambling.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_gambling.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_gambling.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_gambling.meses_previos.porcentaje * 100)  || '0'] },
        { label: 'Resto', totalAmount: [Math.floor(resumen.desglose.gasto_resto_discrecional.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_resto_discrecional.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_resto_discrecional.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_resto_discrecional.meses_previos.porcentaje * 100)  || '0'] },
        
        // { label: 'Educación', totalAmount: [Math.floor(resumen.desglose.gasto_educacion.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_educacion.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_educacion.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_educacion.meses_previos.porcentaje * 100)  || '0'] },
        // { label: 'ONGs', totalAmount: [Math.floor(resumen.desglose.gasto_ongs.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_ongs.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_ongs.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_ongs.meses_previos.porcentaje * 100)  || '0'] },
        // { label: 'Comercio', totalAmount: [Math.floor(resumen.desglose.gasto_comercio.mes_actual.monto) || '0', Math.floor(resumen.desglose.gasto_comercio.meses_previos.promedio) || '0'], value: [Math.floor(resumen.desglose.gasto_comercio.mes_actual.porcentaje * 100)  || '0', Math.floor(resumen.desglose.gasto_comercio.meses_previos.porcentaje * 100)  || '0'] },
    ]

    desgloseGastoBasico = DesgloseAbsoluto(desgloseGastoBasico);
    desgloseGastoDiscrecional = DesgloseAbsoluto(desgloseGastoDiscrecional);

    // Utilidad para mostrar los seguros que tiene
    const seguros = [];
    if (resumen.alertas.tiene_seguro_hogar) seguros.push('Hogar');
    if (resumen.alertas.tiene_seguro_coche) seguros.push('Coche');
    if (resumen.alertas.tiene_seguro_vida) seguros.push('Vida');
    const segurosTexto = seguros.length > 0 ? seguros.join(', ') : 'No';

    const cambioTendenciasTexto = resumen.alertas.cambio_tendencia_mensaje_final || 'No se encontró un cambio de tendencia significativo en los ingresos.';
    
    return(
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1} marginTop={2}>
                    <Iconify icon="material-symbols:warning-outline-rounded" width={28} height={28} />
                    <Typography variant="h4" fontWeight="bold">
                        Alertas últimos 6 meses disponibles
                    </Typography>
                </Stack>
            </Grid>
            <Grid item xs={12}>
                <Card>
                    <Grid container spacing={3} sx={{ p: 2 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                sx={{
                                borderRadius: 3,
                                boxShadow: 'sm',
                                minHeight: 120,
                                display: 'flex',
                                alignItems: 'center',
                                px: 2,
                                py: 1,
                            }}
                            >
                            <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                {
                                    resumen.alertas.tiene_descubierto === true ?
                                        (
                                            <>
                                                <Iconify icon={NegativeIcon} width={60} height={60} color={theme.palette.red.main} />
                                                <Box flexGrow={1} title="En los últimos 6 meses">
                                                    <Typography variant="subtitle2" color={theme.palette.red.main}>
                                                        ¿Descubierto?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.red.main}>
                                                        Sí
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                        :
                                        (
                                            <>
                                                <Iconify icon={PositiveIcon} width={60} height={60} color={theme.palette.tertiary.main} />
                                                <Box flexGrow={1}>
                                                    <Typography variant="subtitle2" color={theme.palette.tertiary.main}>
                                                        ¿Descubierto?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.tertiary.main}>
                                                        No
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                }
                            </Stack>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                sx={{
                                borderRadius: 3,
                                boxShadow: 'sm',
                                minHeight: 120,
                                display: 'flex',
                                alignItems: 'center',
                                px: 2,
                                py: 1,
                            }}
                            >
                            <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                {
                                    resumen.alertas.tiene_embargo === true ?
                                        (
                                            <>
                                                <Iconify icon={NegativeIcon} width={60} height={60} color={theme.palette.red.main} />
                                                <Box flexGrow={1} title="Total de los últimos 6 meses">
                                                    <Typography variant="subtitle2" color={theme.palette.red.main}>
                                                        ¿Embargo?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.red.main}>
                                                        {`Sí, ${fCurrencyEuro(Math.abs(Math.floor(resumen.alertas.monto_embargos)) || '0')}`} 
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                        :
                                        (
                                            <>
                                                <Iconify icon={PositiveIcon} width={60} height={60} color={theme.palette.tertiary.main} />
                                                <Box flexGrow={1}>
                                                    <Typography variant="subtitle2" color={theme.palette.tertiary.main}>
                                                        ¿Embargo?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.tertiary.main}>
                                                        No
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                }
                            </Stack>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                sx={{
                                borderRadius: 3,
                                boxShadow: 'sm',
                                minHeight: 120,
                                display: 'flex',
                                alignItems: 'center',
                                px: 2,
                                py: 1,
                            }}
                            >
                            <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                {
                                    resumen.alertas.tiene_recobro === true ?
                                        (
                                            <>
                                                <Iconify icon={NegativeIcon} width={60} height={60} color={theme.palette.red.main} />
                                                <Box flexGrow={1} title="Total de los últimos 6 meses">
                                                    <Typography variant="subtitle2" color={theme.palette.red.main}>
                                                        ¿Recobros?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.red.main}>
                                                        {`Sí, ${fCurrencyEuro(Math.abs(Math.floor(resumen.alertas.monto_recobro)) || '0')}`} 
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                        :
                                        (
                                            <>
                                                <Iconify icon={PositiveIcon} width={60} height={60} color={theme.palette.tertiary.main} />
                                                <Box flexGrow={1}>
                                                    <Typography variant="subtitle2" color={theme.palette.tertiary.main}>
                                                        ¿Recobros?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.tertiary.main}>
                                                        No
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                }
                            </Stack>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                sx={{
                                borderRadius: 3,
                                boxShadow: 'sm',
                                minHeight: 120,
                                display: 'flex',
                                alignItems: 'center',
                                px: 2,
                                py: 1,
                            }}
                            >
                            <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                {
                                    resumen.alertas.tiene_devolucion_recibo ?
                                        (
                                            <>
                                                <Iconify icon={NegativeIcon} width={60} height={60} color={theme.palette.red.main} />
                                                <Box flexGrow={1} title="Total de los últimos 6 meses">
                                                    <Typography variant="subtitle2" color={theme.palette.red.main}>
                                                        ¿Recibos devueltos?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.red.main}>
                                                        {`Sí, ${fCurrencyEuro(Math.abs(Math.floor(resumen.alertas.monto_devolucion_recibos)) || '0')}`} 
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                        :
                                        (
                                            <>
                                                <Iconify icon={PositiveIcon} width={60} height={60} color={theme.palette.tertiary.main} />
                                                <Box flexGrow={1}>
                                                    <Typography variant="subtitle2" color={theme.palette.tertiary.main}>
                                                        ¿Recibos devueltos?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.tertiary.main}>
                                                        No
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                }
                            </Stack>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                sx={{
                                borderRadius: 3,
                                boxShadow: 'sm',
                                minHeight: 120,
                                display: 'flex',
                                alignItems: 'center',
                                px: 2,
                                py: 1,
                            }}
                            >
                            <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                {
                                    resumen.alertas.tiene_multa === true ? 
                                        (
                                            <>
                                                <Iconify icon={NegativeIcon} width={60} height={60} color={theme.palette.red.main} />
                                                <Box flexGrow={1} title="Total de los últimos 6 meses">
                                                    <Typography variant="subtitle2" color={theme.palette.red.main}>
                                                        ¿Multas o sanciones?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.red.main}>
                                                        {`Sí, ${fCurrencyEuro(Math.abs(Math.floor(resumen.alertas.monto_multa)) || '0')}`} 
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                        :
                                        (
                                            <>
                                                <Iconify icon={PositiveIcon} width={60} height={60} color={theme.palette.tertiary.main} />
                                                <Box flexGrow={1}>
                                                    <Typography variant="subtitle2" color={theme.palette.tertiary.main}>
                                                        ¿Multas o sanciones?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.tertiary.main}>
                                                        No
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                }
                            </Stack>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                sx={{
                                borderRadius: 3,
                                boxShadow: 'sm',
                                minHeight: 120,
                                display: 'flex',
                                alignItems: 'center',
                                px: 2,
                                py: 1,
                            }}
                            >
                            <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                {
                                    resumen.alertas.tiene_microprestamo ? 
                                        (
                                            <>
                                                <Iconify icon={NegativeIcon} width={60} height={60} color={theme.palette.red.main} />
                                                <Box flexGrow={1} title="Total de los últimos 6 meses">
                                                    <Typography variant="subtitle2" color={theme.palette.red.main}>
                                                        ¿Micropréstamos?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.red.main}>
                                                        {`Sí, ${fCurrencyEuro(Math.abs(Math.floor(resumen.alertas.monto_microprestamo)) || '0')}`} 
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                        :
                                        (
                                            <>
                                                <Iconify icon={PositiveIcon} width={60} height={60} color={theme.palette.tertiary.main} />
                                                <Box flexGrow={1}>
                                                    <Typography variant="subtitle2" color={theme.palette.tertiary.main}>
                                                        ¿Micropréstamos?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.tertiary.main}>
                                                        No
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                }
                            </Stack>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                sx={{
                                borderRadius: 3,
                                boxShadow: 'sm',
                                minHeight: 120,
                                display: 'flex',
                                alignItems: 'center',
                                px: 2,
                                py: 1,
                            }}
                            >
                            <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                {
                                    resumen.alertas.tiene_gambling ?
                                        (
                                            <>
                                                <Iconify icon={NegativeIcon} width={60} height={60} color={theme.palette.red.main} />
                                                <Box flexGrow={1} title="Total de los últimos 6 meses">
                                                    <Typography variant="subtitle2" color={theme.palette.red.main}>
                                                        ¿Gambling?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.red.main}>
                                                        {`Sí, ${fCurrencyEuro(Math.abs(Math.floor(resumen.alertas.monto_gambling)) || '0')}`} 
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                        :
                                        (
                                            <>
                                                <Iconify icon={PositiveIcon} width={60} height={60} color={theme.palette.tertiary.main} />
                                                <Box flexGrow={1}>
                                                    <Typography variant="subtitle2" color={theme.palette.tertiary.main}>
                                                        ¿Gambling?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.tertiary.main}>
                                                        No
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                }
                            </Stack>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                sx={{
                                borderRadius: 3,
                                boxShadow: 'sm',
                                minHeight: 120,
                                display: 'flex',
                                alignItems: 'center',
                                px: 2,
                                py: 1,
                            }}
                            >
                            <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                {
                                    resumen.alertas.tiene_alto_endeudamiento ?
                                        (
                                            <>
                                                <Iconify icon={NegativeIcon} width={60} height={60} color={theme.palette.red.main} />
                                                <Box flexGrow={1} title="Total de los últimos 6 meses">
                                                    <Typography variant="subtitle2" color={theme.palette.red.main}>
                                                        ¿Alto endeudamiento?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.red.main}>
                                                        {`Sí, ${fPercentEuro(Math.abs(Math.floor(resumen.alertas.porc_tasa_endeudamiento * 100)) || '0')}`} 
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                        :
                                        (
                                            <>
                                                <Iconify icon={PositiveIcon} width={60} height={60} color={theme.palette.tertiary.main} />
                                                <Box flexGrow={1}>
                                                    <Typography variant="subtitle2" color={theme.palette.tertiary.main}>
                                                        ¿Alto endeudamiento?
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold" color={theme.palette.tertiary.main}>
                                                        No
                                                    </Typography>
                                                </Box>
                                            </>
                                        )
                                }
                            </Stack>
                        </Card>
                    </Grid>
                </Grid>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1} marginTop={2}>
                        <Iconify icon="mdi:cash-plus" width={28} height={28} />
                        <Typography variant="h4" fontWeight="bold">
                            Perfil de ingresos
                        </Typography>
                    </Stack>
                    <Card sx={{ flex:1, height: '100%', display: 'flex', flexDirection: 'column', border: '2px solid #C4CDD5' }}>
                        <CardContent>
                            <Stack spacing={2}>
                                <Box>
                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                        <Card
                                            sx={{
                                                flex: 1,
                                                borderRadius: 3,
                                                boxShadow: 'sm',
                                                minHeight: 120,
                                                display: 'flex',
                                                alignItems: 'center',
                                                px: 2,
                                                py: 1,
                                            }}
                                        >
                                            <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                                <Iconify icon='streamline-plump:office-worker' width={60} height={60} />
                                                <Box flexGrow={1}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Typography variant="subtitle2">
                                                            Régimen de contrato
                                                        </Typography>
                                                        <ClickableTooltip title="Calculado sobre los últimos 30 días disponibles" arrow>
                                                            <IconButton component="span" sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                                <Iconify icon="mdi:information-outline" width={18} height={18} color={theme.palette.text.secondary} />
                                                            </IconButton>
                                                        </ClickableTooltip>
                                                    </Box>
                                                    <Typography variant="h6" fontWeight="bold">
                                                        {resumen.alertas.regimen_contrato || 'No disponible'}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Card>
                                        <Card
                                            sx={{
                                                flex: 1,
                                                borderRadius: 3,
                                                boxShadow: 'sm',
                                                minHeight: 120,
                                                display: 'flex',
                                                alignItems: 'center',
                                                px: 2,
                                                py: 1,
                                            }}
                                        >
                                            <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                                <Iconify icon='material-symbols:payments-outline' width={60} height={60} />
                                                <Box flexGrow={1}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Typography variant="subtitle2">
                                                            Fuente principal de ingresos
                                                        </Typography>
                                                        <ClickableTooltip title="Calculado sobre los últimos 30 días disponibles" arrow>
                                                            <IconButton component="span" sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                                <Iconify icon="mdi:information-outline" width={18} height={18} color={theme.palette.text.secondary} />
                                                            </IconButton>
                                                        </ClickableTooltip>
                                                    </Box>
                                                    <Typography variant="h6" fontWeight="bold">
                                                        {resumen.alertas.fuente_principal_ingresos || 'No disponible'}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Card>
                                    </Stack>
                                </Box>

                                <Box>
                                    <FinancialPercentOverview title={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="h6">Desglose de ingresos mensuales</Typography>
                                            <ClickableTooltip title="Comparativa últimos 30 días con el promedio de los 5 meses anteriores" arrow>
                                            <IconButton component="span" sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                <Iconify icon="mdi:information-outline" width={18} height={18} color={theme.palette.text.secondary} />
                                            </IconButton>
                                            </ClickableTooltip>
                                        </Box>   
                                    } data={desgloseIngresos.filter((data) => data.totalAmount[0] > 0 || data.totalAmount[1] > 0)} />        
                                </Box>

                                <Box>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="subtitle1">Cambio de tendencia:</Typography>
                                        <ClickableTooltip title={
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                <Typography variant="subtitle3" sx={{ fontWeight: 'normal' }}>
                                                    Muestra cambios de tendencia observados en el último mes vs los cinco meses anteriores. En particular, cambios de régimen de contrato, de fuente principal de ingresos y aparición o desaparición de categorías de ingresos.
                                                </Typography>
                                            </Box>
                                        } arrow>
                                        <IconButton component="span" sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                            <Iconify icon="mdi:information-outline" width={18} height={18} color={theme.palette.text.secondary} />
                                        </IconButton>
                                        </ClickableTooltip>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        <ReactMarkdown>{cambioTendenciasTexto}</ReactMarkdown>
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Box>
            </Grid>
            <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1} marginTop={2}>
                        <Iconify icon="mdi:cash-minus" width={28} height={28} />
                        <Typography variant="h4" fontWeight="bold">
                            Perfil de gastos
                        </Typography>
                    </Stack>
                    <Card sx={{ flex:1, height: '100%', display: 'flex', flexDirection: 'column', border: '2px solid #C4CDD5' }}>
                        <CardContent>
                            <Stack spacing={3}>
                                    <Card>
                                        <CardHeader title='Consumo básico' />
                                        <CardContent>
                                            <Stack spacing={2} sx={{ mt: 1, ml: 1 }}>
                                                <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                                    <Card
                                                        sx={{
                                                            flex: 1,
                                                            borderRadius: 3,
                                                            boxShadow: 'sm',
                                                            minHeight: 120,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            px: 2,
                                                            py: 1,
                                                        }}
                                                    >
                                                        <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                                            <Iconify icon='mdi:cart-outline' width={60} height={60} />
                                                            <Box flexGrow={1}>
                                                                <Typography variant="subtitle2">
                                                                    Sobre el total de ingresos
                                                                </Typography>
                                                                <Typography variant="h3" fontWeight="bold">
                                                                    {fPercentEuro(Math.floor(Math.abs(100 * resumen.gasto.estabilidad_gasto_basico_tot_ingreso)) || '0')}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Card>
                                                    <Card
                                                        sx={{
                                                            flex: 1,
                                                            borderRadius: 3,
                                                            boxShadow: 'sm',
                                                            minHeight: 120,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            px: 2,
                                                            py: 1,
                                                        }}
                                                    >
                                                        <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                                            <Iconify icon='mdi:water-pump' width={60} height={60} />
                                                            <Box flexGrow={1}>
                                                                <Typography variant="subtitle2">
                                                                    Sobre el total de gastos
                                                                </Typography>
                                                                <Typography variant="h3" fontWeight="bold">
                                                                    {fPercentEuro(Math.floor(Math.abs(100 * resumen.gasto.estabilidad_gasto_basico_tot_gasto)) || '0')}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Card>
                                                </Stack>
                                                

                                                <Box>
                                                    <FinancialPercentOverview data={desgloseGastoBasico.filter((data) => data.totalAmount[0] > 0 || data.totalAmount[1] > 0)} />        
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardHeader title='Consumo discrecional' />
                                        <CardContent>
                                            <Stack spacing={2} sx={{ mt: 1, ml: 1 }}>
                                                <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                                    <Card
                                                        sx={{
                                                            flex: 1,
                                                            borderRadius: 3,
                                                            boxShadow: 'sm',
                                                            minHeight: 120,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            px: 2,
                                                            py: 1,
                                                        }}
                                                    >
                                                        <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                                            <Iconify icon='mdi:glass-cocktail' width={60} height={60} />
                                                            <Box flexGrow={1}>
                                                                <Typography variant="subtitle2">
                                                                    Sobre el total de ingresos
                                                                </Typography>
                                                                <Typography variant="h3" fontWeight="bold">
                                                                    {fPercentEuro(Math.floor(Math.abs(100 * resumen.gasto.estabilidad_gasto_discrecional_tot_ingreso)) || '0')}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Card>
                                                    <Card
                                                        sx={{
                                                            flex: 1,
                                                            borderRadius: 3,
                                                            boxShadow: 'sm',
                                                            minHeight: 120,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            px: 2,
                                                            py: 1,
                                                        }}
                                                    >
                                                        <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                                            <Iconify icon='material-symbols:shopping-bag-outline' width={60} height={60} />
                                                            <Box flexGrow={1}>
                                                                <Typography variant="subtitle2">
                                                                    Sobre el total de gastos
                                                                </Typography>
                                                                <Typography variant="h3" fontWeight="bold">
                                                                    {fPercentEuro(Math.floor(Math.abs(100 * resumen.gasto.estabilidad_gasto_discrecional_tot_gasto)) || '0')}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Card>
                                                </Stack>
                                                <Box>
                                                    <FinancialPercentOverview 
                                                    // title={
                                                    //     <Box>
                                                    //         <Typography variant="body1">
                                                    //             <strong>Ticket medio de restaurante/fast_food: </strong> {
                                                    //                 (() => {
                                                    //                     const tRest = resumen.gasto.ticket_promedio_restaurantes;
                                                    //                     const tFfood = resumen.gasto.ticket_promedio_ffood;
                                                    //                     if (tRest && tFfood) {
                                                    //                         return fCurrencyEuro(Math.min(Math.floor(tRest), Math.floor(tFfood)));
                                                    //                     }
                                                    //                     if (tRest) return fCurrencyEuro(Math.floor(tRest));
                                                    //                     if (tFfood) return fCurrencyEuro(Math.floor(tFfood));
                                                    //                     return 'No';
                                                    //                 })()
                                                    //             }
                                                    //         </Typography>
                                                    //     </Box>
                                                    // } 
                                                    data={desgloseGastoDiscrecional.filter((data) => data.totalAmount[0] > 0 || data.totalAmount[1] > 0)} />        
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardHeader title='Movimientos' />
                                        <CardContent>
                                            <Stack spacing={2} sx={{ mt: 1, ml: 1 }}>
                                                <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                                    <Card
                                                        sx={{
                                                            flex: 1,
                                                            borderRadius: 3,
                                                            boxShadow: 'sm',
                                                            minHeight: 120,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            px: 2,
                                                            py: 1,
                                                        }}
                                                    >
                                                        <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                                            <Iconify icon='solar:hand-money-linear' width={60} height={60} />
                                                            <Box flexGrow={1}>
                                                                <Typography variant="subtitle2">
                                                                    Sobre el total de ingresos
                                                                </Typography>
                                                                <Typography variant="h3" fontWeight="bold">
                                                                    {fPercentEuro(Math.floor(Math.abs(resumen.gasto.estabilidad_gasto_movimientos_tot_ingreso * 100)) || '0')}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Card>
                                                    <Card
                                                        sx={{
                                                            flex: 1,
                                                            borderRadius: 3,
                                                            boxShadow: 'sm',
                                                            minHeight: 120,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            px: 2,
                                                            py: 1,
                                                        }}
                                                    >
                                                        <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                                            <Iconify icon='mdi:transfer' width={60} height={60} />
                                                            <Box flexGrow={1}>
                                                                <Typography variant="subtitle2">
                                                                    Sobre el total de gastos
                                                                </Typography>
                                                                <Typography variant="h3" fontWeight="bold">
                                                                    {fPercentEuro(Math.floor(Math.abs(resumen.gasto.estabilidad_gasto_movimientos_tot_gasto * 100)) || '0')}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Card>
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                            </Stack>
                        </CardContent>
                    </Card>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box
                    sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: 10,
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify icon="tabler:git-compare" width={28} height={28} />
                    <Typography
                        variant="h4"
                        fontWeight="bold"
                        align="center"
                        sx={{ textAlign: 'center' }}
                    >
                        Comparativa con grupo de referencia
                    </Typography>
                    </Stack>
                </Box>
            </Grid>
            <Grid item xs={12} md={6}>
                <Card sx={{ display: 'flex', flexDirection: 'column' }}>
                    <CardHeader title="Ingresos" />
                    <CardContent>
                        <ChartRadar
                            series={[
                                {
                                    name: 'Grupo de referencia',
                                    data: ingresosCategories.map((item) => item.average)
                                },
                                {
                                    name: 'Agregación analizada',
                                    data: compIngresos
                                },
                            ]}
                            categories={ingresosCategories.map((item) => item.category)}
                            />
                    </CardContent>
                    <CardContent sx={{ borderTop: '1px solid #eee', height: 150, overflow: 'auto' }}>
                        <Typography variant="caption" color="text.secondary" sx={{textAlign: 'justify'}} mb={1}>
                            <ReactMarkdown>
                                {ingresosResume || "Cargando resumen..."}
                            </ReactMarkdown>
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <Card sx={{ display: 'flex', flexDirection: 'column' }}>
                    <CardHeader title="Gastos" />
                    <CardContent>
                        <ChartRadar
                            series={[
                                {
                                    name: 'Grupo de referencia',
                                    data: gastosCategories.map((item) => item.average)
                                },
                                {
                                    name: 'Agregación analizada',
                                    data: compGastos
                                },
                            ]}
                            categories={gastosCategories.map((item) => item.category)}
                            />
                    </CardContent>
                    <CardContent sx={{ borderTop: '1px solid #eee', height: 150, overflow: 'auto' }}>
                        <Typography variant="caption" color="text.secondary" sx={{textAlign: 'justify'}} mb={1}>
                            <ReactMarkdown>
                                {gastosResume || "Cargando resumen..."}
                            </ReactMarkdown>
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    )

}

function DesgloseAbsoluto(data) {
    return data.map((item) => ({
        ...item,
        totalAmount: item.totalAmount.map((amount) => Math.abs(Math.floor(amount)) || '0'),
    }))
}